import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";

/** Where focus goes when the panel opens: its first control, or the prompt box. */
export const DOCK_FOCUS = { first: "first", composer: "composer" } as const;

export type DockFocusTarget = (typeof DOCK_FOCUS)[keyof typeof DOCK_FOCUS];

export interface AgentDockControls {
  open: boolean;
  openDock: (focus?: DockFocusTarget) => void;
  closeDock: () => void;
  launcherRef: RefObject<HTMLButtonElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  /** The prompt box, so a field action can open the panel straight onto it. */
  composerRef: RefObject<HTMLTextAreaElement | null>;
  /** Keeps Tab inside the panel and closes it on Escape. */
  onPanelKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  /** Ids for `aria-labelledby` / `aria-describedby` on the panel. */
  titleId: string;
  descriptionId: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const KEY_TAB = "Tab";
const KEY_ESCAPE = "Escape";

/**
 * Open/closed state for the dock, plus the focus discipline a dialog owes: focus moves into the
 * panel when it opens, Tab cycles inside it, and focus returns to the launcher when it closes.
 */
export function useAgentDock(host: HTMLElement | null = null): AgentDockControls {
  const [open, setOpen] = useState(false);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const focusTarget = useRef<DockFocusTarget>(DOCK_FOCUS.first);
  const titleId = useId();
  const descriptionId = useId();

  // Focus returns to the launcher only after a real close: on mount the page keeps its own focus.
  const wasOpen = useRef(false);

  const openDock = useCallback((focus: DockFocusTarget = DOCK_FOCUS.first) => { focusTarget.current = focus; setOpen(true); }, []);
  const closeDock = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      const target = focusTarget.current === DOCK_FOCUS.composer ? composerRef.current : null;
      (target ?? panelRef.current?.querySelector<HTMLElement>(FOCUSABLE))?.focus();
      return;
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      launcherRef.current?.focus();
    }
  }, [open, host]);

  const onPanelKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (host !== null) return;
    if (event.key === KEY_ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key !== KEY_TAB || panelRef.current === null) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [host]);

  return { open, openDock, closeDock, launcherRef, panelRef, composerRef, onPanelKeyDown, titleId, descriptionId };
}
